import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Tabs,
  Tab,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  alpha,
  Skeleton,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TableChartIcon from "@mui/icons-material/TableChart";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";

import useTasks from "../../Main/hooks/useTasks";
import useAuthUser from "../../../hooks/useAuthUser";
import TaskCalendar from "../components/TaskCalendar";
import TasksTable from "../components/TasksTable";
import TaskDetailDialog from "../components/TaskDetailDialog";
import CalendarLegend from "../components/CalendarLegend";

// ── KPI Card ────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, color, gradient }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        flex: 1,
        minWidth: 140,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -8,
          right: -8,
          width: 60,
          height: 60,
          borderRadius: "50%",
          bgcolor: alpha(color, 0.08),
        }}
      />
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: 2,
              background: gradient,
              color: "white",
            }}
          >
            {icon}
          </Box>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            {label}
          </Typography>
        </Stack>
        <Typography variant="h5" fontWeight={800} sx={{ color }}>
          {value}
        </Typography>
      </Stack>
    </Paper>
  );
}

// ── Main Page ───────────────────────────────────────────────────────

export default function GeneralCalendarPage() {
  const user = useAuthUser();
  const employeeId = user?.employee || user?.employeeId;

  // Cargar todas las tareas (sin filtro de location)
  const { tasks, stats, loading } = useTasks(null);

  // State
  const [selectedTask, setSelectedTask] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewTab, setViewTab] = useState(0); // 0=calendar, 1=table
  const [ownershipFilter, setOwnershipFilter] = useState("all"); // all, mine, delegated

  // Filtrar tareas por ownership
  const filteredTasks = useMemo(() => {
    if (!employeeId) return tasks;
    if (ownershipFilter === "mine") {
      return tasks.filter((t) => String(t.assigneeId) === String(employeeId));
    }
    if (ownershipFilter === "delegated") {
      return tasks.filter(
        (t) =>
          String(t.creatorId) === String(employeeId) &&
          String(t.assigneeId) !== String(employeeId)
      );
    }
    return tasks;
  }, [tasks, employeeId, ownershipFilter]);

  // Conteos
  const myTasksCount = useMemo(
    () => tasks.filter((t) => String(t.assigneeId) === String(employeeId)).length,
    [tasks, employeeId]
  );
  const delegatedCount = useMemo(
    () =>
      tasks.filter(
        (t) =>
          String(t.creatorId) === String(employeeId) &&
          String(t.assigneeId) !== String(employeeId)
      ).length,
    [tasks, employeeId]
  );

  const handleEventClick = (task) => {
    setSelectedTask(task);
    setDialogOpen(true);
  };

  const handleRowClick = (task) => {
    setSelectedTask(task);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="text" width={300} height={40} />
          <Stack direction="row" spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={90} sx={{ flex: 1, borderRadius: 3 }} />
            ))}
          </Stack>
          <Skeleton variant="rounded" height={500} sx={{ borderRadius: 3 }} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      <Stack spacing={3}>
        {/* ── Header ─────────────────────────────────────────────── */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 44,
                height: 44,
                borderRadius: 3,
                background: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
                color: "white",
                boxShadow: "0 4px 14px rgba(102, 126, 234, 0.4)",
              }}
            >
              <CalendarMonthIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800}>
                Schedule
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tasks & Appointments
              </Typography>
            </Box>
          </Stack>

          {/* Filtro ownership */}
          <ToggleButtonGroup
            value={ownershipFilter}
            exclusive
            onChange={(_, v) => v && setOwnershipFilter(v)}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                borderRadius: "10px !important",
                px: 2,
                border: "1px solid",
                borderColor: "divider",
                "&.Mui-selected": {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                  borderColor: "primary.main",
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15),
                  },
                },
              },
            }}
          >
            <ToggleButton value="all">
              <AssignmentIcon sx={{ fontSize: 18, mr: 0.75 }} />
              All ({tasks.length})
            </ToggleButton>
            <ToggleButton value="mine">
              <PersonIcon sx={{ fontSize: 18, mr: 0.75, color: "#3B82F6" }} />
              My Tasks ({myTasksCount})
            </ToggleButton>
            <ToggleButton value="delegated">
              <PersonOutlineIcon sx={{ fontSize: 18, mr: 0.75, color: "#F59E0B" }} />
              Delegated ({delegatedCount})
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* ── KPI Cards ──────────────────────────────────────────── */}
        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ rowGap: 2 }}>
          <KpiCard
            icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
            label="Total Tasks"
            value={stats.totalTasks || 0}
            color="#6366F1"
            gradient="linear-gradient(135deg, #667EEA 0%, #764BA2 100%)"
          />
          <KpiCard
            icon={<PendingActionsIcon sx={{ fontSize: 18 }} />}
            label="Pending"
            value={stats.totalPending || 0}
            color="#D97706"
            gradient="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
          />
          <KpiCard
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
            label="Completed"
            value={stats.totalCompleted || 0}
            color="#059669"
            gradient="linear-gradient(135deg, #10B981 0%, #059669 100%)"
          />
          <KpiCard
            icon={<EventBusyIcon sx={{ fontSize: 18 }} />}
            label="Overdue"
            value={stats.totalOverdue || 0}
            color="#DC2626"
            gradient="linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
          />
        </Stack>

        {/* ── View tabs + Legend ──────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
            sx={{
              px: 2.5,
              py: 1.5,
              bgcolor: (theme) => alpha(theme.palette.background.default, 0.5),
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Tabs
              value={viewTab}
              onChange={(_, v) => setViewTab(v)}
              sx={{
                minHeight: 36,
                "& .MuiTab-root": {
                  minHeight: 36,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                },
              }}
            >
              <Tab
                icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Calendar"
              />
              <Tab
                icon={<TableChartIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <span>Tasks</span>
                    <Chip
                      label={filteredTasks.length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                        color: "primary.main",
                      }}
                    />
                  </Stack>
                }
              />
            </Tabs>
            <CalendarLegend />
          </Stack>

          {/* ── Content ────────────────────────────────────────── */}
          <Box sx={{ p: 2.5 }}>
            {viewTab === 0 ? (
              <TaskCalendar
                tasks={filteredTasks}
                currentEmployeeId={employeeId}
                onEventClick={handleEventClick}
              />
            ) : (
              <TasksTable
                tasks={filteredTasks}
                currentEmployeeId={employeeId}
                onRowClick={handleRowClick}
                loading={loading}
              />
            )}
          </Box>
        </Paper>

        {/* ── Appointments placeholder ───────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px dashed",
            borderColor: "divider",
            textAlign: "center",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Stack spacing={1} alignItems="center">
            <CalendarMonthIcon sx={{ fontSize: 40, color: "text.disabled" }} />
            <Typography variant="body1" fontWeight={600} color="text.secondary">
              Patient Appointments
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Coming soon — Patient appointment scheduling will appear here
            </Typography>
          </Stack>
        </Paper>
      </Stack>

      {/* ── Task Detail Dialog ─────────────────────────────────── */}
      <TaskDetailDialog
        task={selectedTask}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
}
